// src/components/products/index.js
import styled from "styled-components";
import { useMemo } from "react";
import { useParams, Navigate } from "react-router-dom";
import Navigation from "../navigation/header";
import Footer from "../Footer/footer";
import Features from "./Features";
import Benefits from "./Benefits";
import StudentAndStaffResults from "./UseCases";
import { colors } from "../constants/Colors";
import { FaCloudArrowDown as CloudDownload } from "react-icons/fa6";
import { FaExternalLinkAlt as ExternalLink } from "react-icons/fa";  // Added for external link icon
import { productsData } from "./Content";

const Products = () => {
  const { slug } = useParams();

  const product = useMemo(
    () => productsData.find((p) => p.slug === slug),
    [slug]
  );

  if (!product) return <Navigate to="/404" replace />;

  const handleDownload = () => {
    const pdfUrl = product.downloadUrl;
    if (!pdfUrl) return;

    window.open(pdfUrl, "_blank");

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = product.title ? `${product.title}-brief.pdf` : "brief.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleVisitLXP = () => {
    window.open('https://ailms.campusinsights.ai/', '_blank', 'noopener,noreferrer');
  };

  const CTAButton = ({ children, ...props }) => {
    return (
      <ButtonContainer1 {...props}>
        <ButtonText1>{children}</ButtonText1>
        <IconWrapper>
          <CloudDownload color="#fff" />
        </IconWrapper>
      </ButtonContainer1>
    );
  };

  // New Visit LXP Button Component
  const VisitLXPButton = ({ children, ...props }) => {
    return (
      <ButtonContainer2 {...props}>
        <ButtonText2>{children}</ButtonText2>
        <IconWrapper>
          <ExternalLink color="#fff" />
        </IconWrapper>
      </ButtonContainer2>
    );
  };

  /* -------- styles -------- */

  const ButtonContainer1 = styled.button`
  display: flex;
  height: 48px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 16px;
  border: 2px solid rgba(255, 255, 255, 0.7);
  background: 
    linear-gradient(90deg, skyblue, ${colors.PRIMARY_PURPLE}, ${colors.PRIMARY_PURPLE});
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px 0 rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: 240px;
    column-gap: 13px;
  }
`;

  const ButtonContainer2 = styled.button`
  display: flex;
  height: 48px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 16px;
  border: 2px solid rgba(255, 255, 255, 0.7);
  background: 
    linear-gradient(90deg, #00A86B, ${colors.PRIMARY_GREEN || "#2E8B57"}, ${colors.PRIMARY_GREEN || "#2E8B57"});
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px 0 rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: 240px;
    column-gap: 13px;
  }
`;

  const ButtonText1 = styled.span`
  color: #fff;
  text-align: center;
  letter-spacing: -0.075px;
  font: 700 15px/140% Inter, -apple-system, Roboto, Helvetica, sans-serif;
  
  @media (max-width: 640px) {
    font-size: 13px;
  }
`;

  const ButtonText2 = styled.span`
  color: #fff;
  text-align: center;
  letter-spacing: -0.075px;
  font: 700 15px/140% Inter, -apple-system, Roboto, Helvetica, sans-serif;
  
  @media (max-width: 640px) {
    font-size: 13px;
  }
`;

  const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

  const HeroContainer = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 430px;
  overflow: hidden;
  position: relative;
  padding: 140px 20px 40px;

  background:
    linear-gradient(
      #6b37e342 5%,
      #371780dc 50%,
      ${colors.PRIMARY_PURPLE} 100%
    ),
    url(${product?.hero?.image}) top center / cover no-repeat;

  background-blend-mode: normal;

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 640px) {
    padding: 20px 16px;
    height: 490px;
  }
`;

  const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 989px;
  flex-direction: column;
  align-items: center;
  gap: 32px;

  @media (max-width: 991px) {
    max-width: 700px;
    gap: 28px;
  }

  @media (max-width: 640px) {
    margin-top: 70px;
    gap: 24px;
  }
`;

  const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

  const MainHeading = styled.h1`
  width: 100%;
  color: #fff;
  text-align: center;
  text-shadow: 0 4px 4px rgba(0, 0, 0, 0.2);
  letter-spacing: -0.6px;
  font: 700 60px/70px 'Inter', sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 48px;
    line-height: 56px;
  }

  @media (max-width: 640px) {
    font-size: 32px;
    line-height: 40px;
    letter-spacing: -0.4px;
  }
`;

  const Subtitle = styled.p`
  color: #fff;
  text-align: center;
  letter-spacing: -0.08px;
  font: 500 16px/160% 'Inter', sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

  const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 640px) {
    flex-direction: column;
    width: 100%;
    max-width: 240px;
  }
`;

  return (
    <>
      <Navigation />
      <HeroContainer>
        <ContentWrapper>
          <TextContent>
            <MainHeading>{product?.hero?.header || product?.title}</MainHeading>
            <Subtitle>{product?.hero?.description || ""}</Subtitle>
          </TextContent>

          <ButtonGroup>
            {
              slug !== 'ai-lms' && (
                <CTAButton
                  style={{ visibility: product?.hero?.isbutton ? "visible" : "hidden" }}
                  onClick={product?.hero?.isbutton ? handleDownload : null}
                >
                  Download Product Brief
                </CTAButton>
              )
            }
            {/* Conditionally show Visit LXP button for specific products */}
            {slug === 'ai-lms' && (
              <VisitLXPButton onClick={handleVisitLXP}>
                Visit LXP
              </VisitLXPButton>
            )}
          </ButtonGroup>
        </ContentWrapper>
      </HeroContainer>

      {/* Pass product-specific sections */}
      <Features data={product?.features || []} productTitle={product?.title} />
      <Benefits data={product?.benefits || []} />
      <StudentAndStaffResults data={product?.useCases || []} />

      {/* <CTASection /> */}
      <Footer />
    </>
  );

};

export default Products;
